import React, { useState } from 'react';
import { CheckIcon, CopyIcon, DownloadIcon } from 'lucide-react';
import { socialIcons, toSvgString, type SocialIcon as SocialIconData } from '../data/socialIcons';
import { PageHero } from '../components/PageHero';
import { SectionHeading } from '../components/SectionHeading';
import { SocialIcon } from '../components/SocialIcon';
const swatches = [{
  name: 'Black Coffee',
  hex: '#3B302F'
}, {
  name: 'Purple Pennant',
  hex: '#432C47'
}, {
  name: 'Paper',
  hex: '#F3EEE7'
}];

/** /social-icons — internal reference + export sheet for the social set. */
export function SocialIcons() {
  const [colour, setColour] = useState(swatches[0].hex);
  return <div className="w-full bg-paper" data-id="element-881" data-mp="s2jyzc7pngf9">
      <PageHero eyebrow="Social icons" title="A social set drawn for the brand." intro="Seven channels on a 24px grid with a 1.5px round stroke — the same weight as every other icon on the site. Copy any icon straight into Figma, or download it as an SVG." data-id="element-882" data-mp="s1in55ivepwh" />

      {/* ── THE SET ──────────────────────────────────────────── */}
      <section className="mx-auto max-w-[1400px] px-6 py-20 md:py-24" data-id="element-883" data-mp="38g31xim27xm9">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between" data-id="element-884" data-mp="38erzpn89ixx9">
          <SectionHeading eyebrow="01 — The set" title="Export any icon." data-id="element-885" data-mp="33hjbp60fnzo3" />
          <div className="flex flex-wrap items-center gap-2" data-id="element-886" data-mp="2zgfzqulians5">
            <span className="type-eyebrow mr-2 text-[9px] text-coffee/45" data-id="element-887" data-mp="avsrp9n9w0">
              Export colour
            </span>
            {swatches.map(s => <button key={s.hex} type="button" onClick={() => setColour(s.hex)} aria-pressed={colour === s.hex} className={`type-cta flex items-center gap-2 rounded-full border px-4 py-2.5 text-[10px] transition-colors duration-150 ${colour === s.hex ? 'border-coffee text-coffee' : 'border-oat text-coffee/60 hover:border-coffee/50'}`} data-id="element-888" data-mp="3r73xrxrvvd1l">
                <span aria-hidden="true" className="h-3 w-3 rounded-full border border-oat" style={{
              backgroundColor: s.hex
            }} data-id="element-889" data-mp="288pmv3uzflui" />
                {s.name}
              </button>)}
          </div>
        </div>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-890" data-mp="2378n5bo8i5fi">
          {socialIcons.map(icon => <IconTile key={icon.id} icon={icon} colour={colour} data-id="element-891" data-mp="1pfbxrfdya9mt" />)}
        </div>
      </section>

      {/* ── TREATMENTS ───────────────────────────────────────── */}
      <section className="w-full bg-paper-warm py-20 md:py-24" data-id="element-892" data-mp="1fktpry1i3rx7">
        <div className="mx-auto max-w-[1400px] px-6" data-id="element-893" data-mp="1khsf83a7zap2">
          <SectionHeading eyebrow="02 — Treatments" title="Three ways to use them." subtitle="Keep to one treatment per placement. The outlined circle is the default for the footer." data-id="element-894" data-mp="1kg92unropjsk" />

          <div className="mt-14 grid gap-6 lg:grid-cols-3" data-id="element-895" data-mp="rvlufw1vs2e2">
            {/* Bare */}
            <Treatment title="Bare glyph" note="Inline with text — contact details, share links. 20px, no container." bg="bg-paper" data-id="element-896" data-mp="mwr0bxygqren">
              {socialIcons.map(i => <span key={i.id} className="text-coffee" data-id="element-897" data-mp="wuov74atvmg8">
                  <SocialIcon icon={i} size={20} data-id="element-898" data-mp="wxb3j65jm5zi" />
                </span>)}
            </Treatment>

            {/* Outline circle on dark */}
            <Treatment title="Outlined circle" note="Footer and dark sections. 40px circle, 1px Paper @ 25% border, 18px glyph." bg="bg-coffee" data-id="element-899" data-mp="15utv6tdozikd">
              {socialIcons.map(i => <span key={i.id} className="flex h-10 w-10 items-center justify-center rounded-full border border-paper/25 text-paper" data-id="element-900" data-mp="1fnslcfou7h63">
                  <SocialIcon icon={i} size={18} data-id="element-901" data-mp="1ok1l5vgq8hsr" />
                </span>)}
            </Treatment>

            {/* Filled circle on light */}
            <Treatment title="Filled circle" note="Light sections and email footers. 40px Black Coffee circle, Paper glyph." bg="bg-paper" data-id="element-902" data-mp="3o0e7vy996tbp">
              {socialIcons.map(i => <span key={i.id} className="flex h-10 w-10 items-center justify-center rounded-full bg-coffee text-paper" data-id="element-903" data-mp="qg3tlz697t2">
                  <SocialIcon icon={i} size={18} data-id="element-904" data-mp="3533id5dfbe9g" />
                </span>)}
            </Treatment>
          </div>
        </div>
      </section>

      {/* ── RULES ────────────────────────────────────────────── */}
      <section className="mx-auto max-w-[1400px] px-6 py-20 md:py-24" data-id="element-905" data-mp="3e0vnjgpfl5j4">
        <SectionHeading eyebrow="03 — Rules" title="Keeping them on-brand." data-id="element-906" data-mp="odeiqf7n87ov" />
        <div className="mt-14 grid gap-x-12 gap-y-10 md:grid-cols-2 lg:grid-cols-4" data-id="element-907" data-mp="tbzlaladb3gh">
          {[{
          title: 'One colour only',
          body: 'Black Coffee, Purple Pennant or Paper. Never the platforms’ own brand colours — they fight the palette.'
        }, {
          title: 'Stroke, not fill',
          body: '1.5px round stroke at 24px. When scaling in Figma, scale the stroke with it so the weight stays even.'
        }, {
          title: 'Hover is quiet',
          body: 'On hover the border goes to full opacity, or the fill shifts to Purple Pennant. No scaling, no bounce.'
        }, {
          title: 'Four in the footer',
          body: 'Instagram, Facebook, TikTok and Pinterest. YouTube, LinkedIn and WhatsApp are for the contact and corporate pages.'
        }].map(r => <div key={r.title} className="border-t border-oat pt-7" data-id="element-908" data-mp="255rttm3hic1r">
              <h3 className="type-display text-2xl text-coffee" data-id="element-909" data-mp="214gcs4yoez56">{r.title}</h3>
              <p className="type-body mt-3 text-sm text-ink/65" data-id="element-910" data-mp="3unoysciviqnv">{r.body}</p>
            </div>)}
        </div>
      </section>
    </div>;
}
function IconTile({
  icon,
  colour
}: {
  icon: SocialIconData;
  colour: string;
}) {
  const [copied, setCopied] = useState(false);
  const svg = toSvgString(icon, colour, 24);
  const dark = colour === '#F3EEE7';
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(svg);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };
  const download = () => {
    const blob = new Blob([svg], {
      type: 'image/svg+xml'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ecoffee-${icon.id}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return <div className="flex flex-col overflow-hidden rounded-3xl border border-oat/70 bg-paper-warm" data-id="element-911" data-mp="2swrzvi7k7rsp">
      <div className={`flex h-40 items-center justify-center ${dark ? 'bg-coffee' : 'bg-paper'}`} style={{
      color: colour
    }} data-id="element-912" data-mp="2xyhdn5m62jkh">
        <SocialIcon icon={icon} size={48} data-id="element-913" data-mp="2ldiqhoz1zt3n" />
      </div>
      <div className="flex flex-1 flex-col p-6" data-id="element-914" data-mp="2lgwqwro5ps8g">
        <h3 className="type-display text-2xl text-coffee" data-id="element-915" data-mp="2zda8e0soxtmx">{icon.name}</h3>
        <p className="type-body mt-1 text-xs text-ink/50" data-id="element-916" data-mp="2ueqp685hevy9">{icon.handle}</p>
        <div className="mt-auto flex gap-2 pt-6" data-id="element-917" data-mp="2udfh3w50721c">
          <button type="button" onClick={copy} className="type-cta flex flex-1 items-center justify-center gap-2 rounded-full bg-coffee px-4 py-3 text-[10px] text-paper transition-colors duration-150 hover:bg-pennant" data-id="element-918" data-mp="26qftrkik1ohd">
            {copied ? <CheckIcon className="h-3.5 w-3.5" strokeWidth={2} data-id="element-919" data-mp="2bnpq7k4d30ep" /> : <CopyIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-920" data-mp="1nyempuvmmrwv" />}
            {copied ? 'Copied' : 'Copy SVG'}
          </button>
          <button type="button" onClick={download} aria-label={`Download ${icon.name} SVG`} className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-full border border-coffee/25 text-coffee transition-colors duration-150 hover:bg-coffee hover:text-paper" data-id="element-921" data-mp="1iztn9xo78c9h">
            <DownloadIcon className="h-4 w-4" strokeWidth={1.5} data-id="element-922" data-mp="1cdrl2mxqu75z" />
          </button>
        </div>
      </div>
    </div>;
}
function Treatment({
  title,
  note,
  bg,
  children
}: {
  title: string;
  note: string;
  bg: string;
  children: React.ReactNode;
}) {
  return <div className="flex flex-col overflow-hidden rounded-4xl border border-oat/70 bg-paper" data-id="element-923" data-mp="1lcms218m3lac">
      <div className={`flex flex-wrap items-center justify-center gap-4 px-8 py-14 ${bg}`} data-id="element-924" data-mp="1q7it1qvttizl">
        {children}
      </div>
      <div className="p-8" data-id="element-925" data-mp="203ls2nvdt1ib">
        <h3 className="type-display text-2xl text-coffee" data-id="element-926" data-mp="204evpfz1naw8">{title}</h3>
        <p className="type-body mt-2 text-sm text-ink/65" data-id="element-927" data-mp="244xnxb4umiyy">{note}</p>
      </div>
    </div>;
}